/**
 * @file status-update.service.ts
 * @module orders-delivery/services/status-update
 * @description Consolidated API service for route and order status updates.
 * Unifies vehicleRouteStatus, vehicleOrderStatus, and updateOrder API calls
 * across goods-loading-details, route-details, and review-orders-list components.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.1
 * @version 2.1.2
 */

import APICallService from '../../../../api/apiCallService';
import { ordersDelivery, routePlanning } from '../../../../api/apiEndPoints';
import { routePlanningJSON } from '../../../../api/apiJSON/routePlanning';
import { OrdersDelivery } from '../../../../utils/constants';

/**
 * Update vehicle route status (e.g., cancelled, delivered, out-for-delivery).
 *
 * @param {number} status - Route order status constant (e.g., RouteOrderCancelled)
 * @param {string} routeId - Route plan ID
 * @param {string} vehicleId - Vehicle reference ID
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @param {string} [cancellationReason] - Reason template key; required by the API when cancelling [@see ROUTE_PLAN_CANCELLATION_REASONS]
 * @param {string} [cancellationNote] - Free text; required when the reason key is OTHER
 * @returns {Promise<any>} API response
 */
export async function updateVehicleRouteStatus(
  status: number,
  routeId: string,
  vehicleId: string,
  warehouseId: string | null,
  cancellationReason?: string,
  cancellationNote?: string
): Promise<any> {
  const apiService = new APICallService(
    routePlanning.vehicleRouteStatus,
    routePlanningJSON.vehicleRouteStatusUpdate({
      status,
      cancellationReason,
      cancellationNote,
    }),
    { routeId, vehicleId },
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}

/**
 * Uploads the loading-complete photo for a route vehicle via the dedicated mark-loaded endpoint.
 * This only sets the proof-of-loading image; it does not change vehicle/order status.
 *
 * @param {string} capturedImage - Captured photo (data URL)
 * @param {string} routeId - Route plan ID
 * @param {string} vehicleId - Vehicle reference ID
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function markVehicleGoodsAsLoaded(
  capturedImage: string,
  routeId: string,
  vehicleId: string,
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    routePlanning.vehicleMarkAsLoaded,
    routePlanningJSON.markGoodsAsLoadedUpdate(capturedImage),
    { routeId, vehicleId },
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}

/**
 * Update vehicle order status (e.g., delivered, moved to pending, partial payment).
 *
 * @param {any} statusUpdateData - Status update payload (object or FormData)
 * @param {string} routeId - Route plan ID
 * @param {string} vehicleId - Vehicle reference ID
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function updateVehicleOrderStatus(
  statusUpdateData: any,
  routeId: string,
  vehicleId: string,
  warehouseId: string | null
): Promise<any> {
  // FormData is already fully built by the caller (e.g. for image uploads); only plain objects need formatting
  const payload =
    statusUpdateData instanceof FormData
      ? statusUpdateData
      : routePlanningJSON.vehicleOrderStatusUpdate(statusUpdateData);
  const apiService = new APICallService(
    routePlanning.vehicleOrderStatus,
    payload,
    { routeId, vehicleId },
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}

/**
 * Update order details (quantity, product, buyer, address, etc.).
 * Used when modifying orders within a route/vehicle plan.
 *
 * @param {any} orderUpdateData - Order data { record, buyer, address, ... }
 * @param {string} routeId - Route plan ID
 * @param {string} vehicleId - Vehicle reference ID
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function updateOrder(
  orderUpdateData: any,
  routeId: string,
  vehicleId: string,
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    routePlanning.updateOrder,
    orderUpdateData,
    { routeId, vehicleId },
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}

/**
 * Marks a completed route plan as reviewed, closing out past-delivery review.
 *
 * @param {string} routeId - Route plan ID
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function completeRouteReview(
  routeId: string,
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    routePlanning.completeReview,
    {},
    { id: routeId },
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}
